import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import DataSource from 'devextreme/data/data_source'
import Form, { ButtonItem, SimpleItem, EmptyItem, GroupItem, ColCountByScreen, Label } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import CustomStore from 'devextreme/data/custom_store'
import { useSnackbar } from 'notistack'
import utilServices from 'app/services/util-services'
import { useSelector } from 'react-redux'
import familyAndMemberService from 'app/services/pages/reports/registrations/familyAndMemberService/familyAndMemberService'
import termServices from 'app/services/pages/courses/setup/termServices/termServices'
import classesService from 'app/services/pages/courses/setup/classesServices/classesService'
import sectionService from 'app/services/pages/courses/setup/sectionServices/sectionService'
import customerServices from 'app/services/pages/reports/registrations/CustomerServices/customerServices'
import { IconButton, Stack, useMediaQuery } from '@mui/material'
import { termStartEndDate } from 'app/services/utils/components/termStartEndDate/termStartEndDate'
import WestIcon from '@mui/icons-material/West'

const UpdateEnrolmentForm = (props) => {
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const { handleSubmit } = useForm()
    const { loading } = useSelector((state) => state.enrolments)
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')

    const [selection, setSelection] = useState({
        termId: props.enrolmentData.termId,
        classId: props.enrolmentData.classId,
        sectionId: props.enrolmentData.sectionId,
        familyId: props.enrolmentData.familyId,
    })

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const onTermValueChanged = (e) => {
        setSelection({
            ...selection,
            termId: e.value,
        })
    }

    const termLookupList = useCallback(async (searchTerm) => {
        try {
            return await termServices.getTermLookupList({ termYear: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const termLookupById = useCallback(async (key) => {
        try {
            return await termServices.getTermLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const termLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await termLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return await utilServices.emptyList()

                    return termLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const termYearEditorOptions = useMemo(
        () => ({
            dataSource: termLookupsStore,
            displayExpr: termStartEndDate,
            valueExpr: 'id',
            onValueChanged: onTermValueChanged,
            readOnly: true,
            searchEnabled: true,
            showClearButton: true,
            itemTemplate: termStartEndDate,
        }),
        [termLookupsStore]
    )

    const onClassValueChanged = (e) => {
        setSelection({
            ...selection,
            classId: e.value,
            sectionId: null,
        })
    }

    const classLookupList = useCallback(
        async (searchTerm) => {
            try {
                if (utilServices.isNullOrUndefined(selection.termId)) return { totalCount: 0, data: [] }
                return await classesService.getClassLookupList({ termId: selection.termId, className: searchTerm })
            } catch (error) {
                handleError(error)
            }
        },
        [selection.termId]
    )

    const classLookupById = useCallback(async (key) => {
        try {
            return await classesService.getClassLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const classLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await classLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return await utilServices.emptyList()

                    return classLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [classLookupList])

    const classNameEditorOptions = useMemo(
        () => ({
            dataSource: classLookupsStore,
            displayExpr: 'className',
            valueExpr: 'id',
            readOnly: true,
            onValueChanged: onClassValueChanged,
            placeholder: 'Require a term selection',
            searchEnabled: true,
            showClearButton: true,
        }),
        [classLookupsStore]
    )

    const sectionLookupList = useCallback(
        async (searchTerm) => {
            try {
                if (utilServices.isNullOrUndefined(selection.classId)) return { totalCount: 0, data: [] }
                return await sectionService.getSectionLookupList({
                    classId: selection.classId,
                    sectionName: searchTerm,
                })
            } catch (error) {
                handleError(error)
            }
        },
        [selection.classId]
    )

    const sectionLookupById = useCallback(async (key) => {
        try {
            return await sectionService.getSectionLookupById(selection.classId, key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const sectionLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await sectionLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()

                    return sectionLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [sectionLookupList])

    const sectionNameEditorOptions = useMemo(
        () => ({
            dataSource: sectionLookupsStore,
            displayExpr: 'sectionName',
            valueExpr: 'id',
            value: selection.sectionId,
            onValueChanged: (e) =>
                setSelection({
                    ...selection,
                    sectionId: e.value,
                }),
            placeholder: 'Require a term and class',
            searchEnabled: true,
            showClearButton: true,
        }),
        []
    )

    const onFamilyValueChanged = (e) => {
        setSelection({
            ...selection,
            familyId: e.value,
        })
    }

    const familyLookupList = useCallback(async (searchTerm) => {
        try {
            return await familyAndMemberService.getFamilyLookupList({ familyName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const familyLookupById = useCallback(async (key) => {
        try {
            return await familyAndMemberService.getFamilyLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const familyLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await familyLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()

                    return familyLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const familyNameEditorOptions = useMemo(
        () => ({
            dataSource: familyLookupsStore,
            displayExpr: function (data) {
                return data ? data.familyName.toFamilyContactName(data.contactName) : ''
            },
            readOnly: true,
            valueExpr: 'id',
            searchEnabled: true,
            showClearButton: true,
            onValueChanged: onFamilyValueChanged,
        }),
        [familyLookupsStore]
    )

    const memberLookupList = useCallback(
        async (searchTerm) => {
            try {
                if (utilServices.isNullOrUndefined(selection.familyId)) return { totalCount: 0, data: [] }
                return await familyAndMemberService.getMemberLookupList({
                    familyId: selection.familyId,
                    firstName: searchTerm,
                })
            } catch (error) {
                handleError(error)
            }
        },
        [selection.familyId]
    )

    const memberLookupById = useCallback(async (key) => {
        try {
            if (selection?.familyId) {
                return await familyAndMemberService.getMemberLookupById(selection.familyId, key)
            }
        } catch (error) {
            handleError(error)
        }
    }, [])

    const memberStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await memberLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()

                    return memberLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [memberLookupList])

    const memberEditorOptions = useMemo(
        () => ({
            dataSource: memberStore,
            itemTemplate: function (data) {
                return data ? data.firstName.toFullName(data.lastName) : ''
            },
            displayExpr: function (data) {
                return data ? data.firstName.toFullName(data.lastName) : ''
            },
            valueExpr: 'id',
            searchEnabled: true,
            showClearButton: true,
        }),
        [memberStore]
    )

    const customerLookupList = useCallback(async (searchTerm) => {
        try {
            return await customerServices.getCustomerLookupList({ firstName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const customerLookupById = useCallback(async (key) => {
        try {
            return await customerServices.getCustomerLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const customerLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await customerLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return await utilServices.emptyList()

                    return customerLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const customerEditorOptions = useMemo(
        () => ({
            dataSource: customerLookupsStore,
            displayExpr: function (data) {
                return data ? data.firstName.toFullName(data.lastName) : ''
            },
            valueExpr: 'id',
            readOnly: true,
            searchEnabled: true,
            showClearButton: true,
        }),
        [customerLookupsStore]
    )

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () =>
            navigate('/registrations/enrolments', {
                state: { navigateFrom: 'cancel-update-enrolments' },
            }),
    }

    const validationRules = {
        termYear: [{ type: 'required', message: 'Term name is required.' }],
        className: [{ type: 'required', message: 'Class name is required.' }],
        customerName: [{ type: 'required', message: 'Customer name is required.' }],
        firstName: [{ type: 'required', message: 'First name is required.' }],
        lastName: [{ type: 'required', message: 'Last name is required.' }],
        email: [
            { type: 'required', message: 'Email is required.' },
            { type: 'email', message: 'Email is invalid.' },
        ],
        phoneNumber: [],
    }

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/registrations/enrolments`, {
                                    state: { navigateFrom: 'cancel-update-enrolments' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Update Enrolment
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form onSubmit={handleSubmit(props.updateEnrolment)}>
                    <Form
                        mode='form'
                        formData={props.enrolmentData}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem caption='Update Enrolment' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <SimpleItem
                                    dataField='termId'
                                    editorType='dxSelectBox'
                                    editorOptions={termYearEditorOptions}
                                    validationRules={validationRules.termName}>
                                    <Label text='Term' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='classId'
                                    editorType='dxSelectBox'
                                    editorOptions={classNameEditorOptions}
                                    validationRules={validationRules.className}>
                                    <Label text='Class Name' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='sectionId'
                                    editorType='dxSelectBox'
                                    editorOptions={sectionNameEditorOptions}>
                                    <Label text='Section Name' />
                                </SimpleItem>
                                <EmptyItem />
                                <GroupItem>
                                    <Divider />
                                </GroupItem>
                                <EmptyItem />
                                <SimpleItem
                                    dataField='familyId'
                                    editorType='dxSelectBox'
                                    editorOptions={familyNameEditorOptions}>
                                    <Label text='Family Name' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='memberId'
                                    editorType='dxSelectBox'
                                    editorOptions={memberEditorOptions}>
                                    <Label text='Member Name' />
                                </SimpleItem>
                                <EmptyItem />
                                <GroupItem>
                                    <Divider />
                                </GroupItem>
                                <EmptyItem />
                                <SimpleItem
                                    dataField='customerId'
                                    editorType='dxSelectBox'
                                    editorOptions={customerEditorOptions}
                                    validationRules={validationRules.customerName}>
                                    <Label text='Customer Name' />
                                </SimpleItem>
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default UpdateEnrolmentForm
